import { useAuth } from '@berky/auth'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Textarea,
  Label,
  LoadingSkeleton,
  ErrorState,
  ModuleIcon,
  PageHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  toast,
} from '@berky/ui'
import { ArrowDown, ArrowUp, Check, LoaderCircle, Plus, Save } from 'lucide-react'
import { useState } from 'react'
import { Navigate } from 'react-router'

import {
  useCreateNavigationCategory,
  useModuleNavigation,
  useNavigationCategories,
  useSaveModuleNavigation,
  useSaveNavigationCategories,
} from './api'
import {
  type ModuleNavigationAssignment,
  type ModuleNavigationSetting,
  type NavigationCategory,
} from './types'
import { roleAccess } from '../module-access'
import { modules } from '../modules'
import { PLATFORM_ADMIN_ROLE } from '../verwaltung/api'

const UNASSIGNED = 'Nicht zugeordnet'
const MINIMUM_SAVE_FEEDBACK_MS = 600

export function ModuleNavigationPage({
  section = 'modules',
}: {
  section?: 'modules' | 'navigation'
}) {
  const { user, isLoading } = useAuth()
  const access = roleAccess(user?.roles, [PLATFORM_ADMIN_ROLE])
  const navigation = useModuleNavigation()
  const categoriesQuery = useNavigationCategories()
  const save = useSaveModuleNavigation()
  const [draft, setDraft] = useState<ModuleNavigationSetting[] | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const settings = draft ?? navigation.data ?? []
  const categories = (categoriesQuery.data ?? []).toSorted(
    (left, right) => left.sortOrder - right.sortOrder,
  )

  if (isLoading || access === 'unbekannt') {
    return <LoadingSkeleton variant="text" rows={5} />
  }

  if (access !== 'erlaubt') {
    return (
      <Navigate
        to="/forbidden"
        replace
        state={{ moduleTitle: section === 'navigation' ? 'Navigation' : 'Module' }}
      />
    )
  }

  if (navigation.isPending || categoriesQuery.isPending) {
    return <LoadingSkeleton variant="text" rows={5} />
  }

  if (navigation.isError || categoriesQuery.isError) {
    return (
      <ErrorState
        description="Die Navigation konnte nicht geladen werden."
        onRetry={() => {
          void navigation.refetch()
          void categoriesQuery.refetch()
        }}
      />
    )
  }

  if (section === 'navigation') {
    return (
      <>
        <PageHeader
          title="Navigation"
          description="Verwalten Sie Rubriken, deren Darstellung und Reihenfolge in der Plattformnavigation."
        />
        <CategoryManagement categories={categories} />
      </>
    )
  }

  function update(moduleId: string, change: Partial<ModuleNavigationSetting>) {
    if (isSaving) return
    setSaveStatus('idle')
    setDraft((currentDraft) =>
      normalizeOrders(
        (currentDraft ?? navigation.data ?? []).map((entry) =>
          entry.moduleId === moduleId ? { ...entry, ...change } : entry,
        ),
      ),
    )
  }

  function updateCategory(
    moduleId: string,
    categoryId: ModuleNavigationAssignment['categoryId'],
    isAssigned: boolean,
  ) {
    if (isSaving) return
    setSaveStatus('idle')
    setDraft((currentDraft) => {
      const current = currentDraft ?? navigation.data ?? []
      const entry = current.find((item) => item.moduleId === moduleId)
      if (!entry) return current

      const alreadyAssigned = entry.categories.some(
        (assignment) => assignment.categoryId === categoryId,
      )
      if (alreadyAssigned === isAssigned) return current

      const categories = isAssigned
        ? [
            ...entry.categories,
            {
              categoryId,
              sortOrder:
                Math.max(
                  -1,
                  ...current.flatMap((item) =>
                    item.categories
                      .filter((assignment) => assignment.categoryId === categoryId)
                      .map((assignment) => assignment.sortOrder),
                  ),
                ) + 1,
            },
          ]
        : entry.categories.filter((assignment) => assignment.categoryId !== categoryId)

      return normalizeOrders(
        current.map((item) =>
          item.moduleId === moduleId
            ? {
                ...item,
                categories,
                ...(categories.length === 0 ? { isVisible: false } : {}),
              }
            : item,
        ),
      )
    })
  }

  function move(
    moduleId: string,
    categoryId: ModuleNavigationAssignment['categoryId'] | null,
    direction: -1 | 1,
  ) {
    if (isSaving) return
    setSaveStatus('idle')
    setDraft((currentDraft) => {
      const current = currentDraft ?? navigation.data ?? []
      if (!categoryId) return current

      const entry = current.find((item) => item.moduleId === moduleId)
      const assignment = entry?.categories.find((item) => item.categoryId === categoryId)
      if (!entry || !assignment) return current

      const categoryEntries = current
        .flatMap((item) => {
          const categoryAssignment = item.categories.find(
            (assignment) => assignment.categoryId === categoryId,
          )
          return categoryAssignment
            ? [{ moduleId: item.moduleId, assignment: categoryAssignment }]
            : []
        })
        .sort((a, b) => a.assignment.sortOrder - b.assignment.sortOrder)
      const index = categoryEntries.findIndex((item) => item.moduleId === moduleId)
      const other = categoryEntries[index + direction]
      if (!other) return current

      return current.map((item) => {
        if (item.moduleId === entry.moduleId) {
          return {
            ...item,
            categories: item.categories.map((itemAssignment) =>
              itemAssignment.categoryId === categoryId
                ? { ...itemAssignment, sortOrder: other.assignment.sortOrder }
                : itemAssignment,
            ),
          }
        }
        if (item.moduleId === other.moduleId) {
          return {
            ...item,
            categories: item.categories.map((itemAssignment) =>
              itemAssignment.categoryId === categoryId
                ? { ...itemAssignment, sortOrder: assignment.sortOrder }
                : itemAssignment,
            ),
          }
        }
        return item
      })
    })
  }

  async function submit() {
    if (saveStatus === 'saving' || saveStatus === 'saved') return

    setSaveStatus('saving')
    const startedAt = performance.now()
    try {
      await save.mutateAsync(normalizeOrders(settings))
      const remaining = MINIMUM_SAVE_FEEDBACK_MS - (performance.now() - startedAt)
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining))
      }
      setDraft(null)
      setSaveStatus('saved')
      toast.success('Navigation gespeichert')
    } catch {
      setSaveStatus('idle')
      toast.error('Navigation konnte nicht gespeichert werden')
    }
  }

  const isSaving = save.isPending || saveStatus === 'saving'
  const saveLabel =
    saveStatus === 'saving'
      ? 'Speichere Navigation …'
      : saveStatus === 'saved'
        ? 'Gespeichert'
        : 'Änderungen speichern'

  return (
    <>
      <PageHeader
        title="Module"
        description="Ordnen Sie Anwendungen einem oder mehreren Bereichen zu und bestimmen Sie Sichtbarkeit und Reihenfolge."
        actions={
          <Button
            onClick={submit}
            disabled={isSaving || saveStatus === 'saved'}
            aria-busy={isSaving}
          >
            {saveStatus === 'saving' ? (
              <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
            ) : saveStatus === 'saved' ? (
              <Check className="size-4" aria-hidden="true" />
            ) : (
              <Save className="size-4" aria-hidden="true" />
            )}
            {saveLabel}
          </Button>
        }
      />

      <fieldset disabled={isSaving} className="min-w-0 space-y-4">
        {categories
          .filter((category) => category.isActive)
          .map((category) => (
            <CategoryBlock
              key={category.id}
              title={category.title}
              settings={settings}
              category={category.id}
              categories={categories}
              onUpdate={update}
              onUpdateCategory={updateCategory}
              onMove={move}
            />
          ))}

        {settings.some((setting) => setting.categories.length === 0) ? (
          <CategoryBlock
            title={UNASSIGNED}
            settings={settings}
            category={null}
            categories={categories}
            onUpdate={update}
            onUpdateCategory={updateCategory}
            onMove={move}
          />
        ) : null}
      </fieldset>
    </>
  )
}

interface CategoryBlockProps {
  title: string
  settings: ModuleNavigationSetting[]
  category: string | null
  categories: NavigationCategory[]
  onUpdate: (moduleId: string, change: Partial<ModuleNavigationSetting>) => void
  onUpdateCategory: (moduleId: string, categoryId: string, isAssigned: boolean) => void
  onMove: (moduleId: string, categoryId: string | null, direction: -1 | 1) => void
}

interface CategoryEntry {
  setting: ModuleNavigationSetting
  assignment: ModuleNavigationAssignment | null
}

function CategoryBlock({
  title,
  settings,
  category,
  categories,
  onUpdate,
  onUpdateCategory,
  onMove,
}: CategoryBlockProps) {
  const entries: CategoryEntry[] = settings
    .flatMap<CategoryEntry>((setting) => {
      if (category === null) {
        return setting.categories.length === 0 ? [{ setting, assignment: null }] : []
      }
      const assignment = setting.categories.find((item) => item.categoryId === category)
      return assignment ? [{ setting, assignment }] : []
    })
    .sort((a, b) => (a.assignment?.sortOrder ?? 0) - (b.assignment?.sortOrder ?? 0))
  const headingId = `category-${category ?? 'unassigned'}`

  return (
    <section aria-labelledby={headingId}>
      <Card className="gap-0 overflow-hidden py-0">
        <div className="flex items-center justify-between border-b bg-muted/40 px-5 py-3">
          <h2 id={headingId} className="font-semibold text-base">
            {title}
          </h2>
          <span className="text-muted-foreground text-sm">
            {entries.length} {entries.length === 1 ? 'Modul' : 'Module'}
          </span>
        </div>

        <CardContent className="divide-y p-0">
          {entries.length === 0 ? (
            <p className="px-5 py-5 text-muted-foreground text-sm">
              Diesem Bereich sind noch keine Module zugeordnet.
            </p>
          ) : (
            entries.map(({ setting }, position) => {
              const module = modules.find((entry) => entry.id === setting.moduleId)
              if (!module) return null

              return (
                <div
                  key={setting.moduleId}
                  className="grid items-center gap-4 px-5 py-3 sm:grid-cols-2 xl:grid-cols-[minmax(14rem,1fr)_minmax(12rem,16rem)_auto_auto]"
                >
                  <div className="flex items-center gap-3">
                    <span className="grid size-9 place-items-center rounded-md bg-secondary">
                      <ModuleIcon name={module.icon} className="size-5" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">{module.title}</span>
                      <span className="block font-normal text-muted-foreground text-xs">
                        {module.id}
                      </span>
                    </span>
                  </div>

                  <CategoryPicker
                    moduleTitle={module.title}
                    categories={setting.categories}
                    options={categories}
                    onChange={(option, isAssigned) =>
                      onUpdateCategory(setting.moduleId, option, isAssigned)
                    }
                  />

                  <div className="flex min-h-10 items-center gap-3">
                    <Switch
                      id={`visible-${module.id}-${category ?? 'unassigned'}`}
                      checked={setting.isVisible}
                      disabled={setting.categories.length === 0}
                      onCheckedChange={(isVisible) =>
                        onUpdate(setting.moduleId, { isVisible })
                      }
                    />
                    <label
                      htmlFor={`visible-${module.id}-${category ?? 'unassigned'}`}
                      className="text-sm font-medium"
                    >
                      Sichtbar
                    </label>
                  </div>

                  <div
                    className="flex gap-1"
                    aria-label={`Reihenfolge für ${module.title}`}
                  >
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!category || position <= 0}
                      onClick={() => onMove(setting.moduleId, category, -1)}
                      aria-label={`${module.title} nach oben`}
                    >
                      <ArrowUp className="size-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!category || position === entries.length - 1}
                      onClick={() => onMove(setting.moduleId, category, 1)}
                      aria-label={`${module.title} nach unten`}
                    >
                      <ArrowDown className="size-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              )
            })
          )}
        </CardContent>
      </Card>
    </section>
  )
}

function CategoryPicker({
  moduleTitle,
  categories,
  options,
  onChange,
}: {
  moduleTitle: string
  categories: ModuleNavigationAssignment[]
  options: NavigationCategory[]
  onChange: (categoryId: string, isAssigned: boolean) => void
}) {
  const selectedCategories = categories.map((assignment) => assignment.categoryId)
  const selectedTitles = options
    .filter((option) => selectedCategories.includes(option.id))
    .map((option) => option.title)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start text-left"
          aria-label={`Bereiche für ${moduleTitle}`}
        >
          <span className="truncate">
            {selectedTitles.length > 0 ? selectedTitles.join(', ') : UNASSIGNED}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64">
        <p className="mb-2 text-sm font-medium">Bereiche für {moduleTitle}</p>
        <div className="grid gap-2">
          {options
            .filter((option) => option.isActive)
            .map((option) => {
              const isAssigned = selectedCategories.includes(option.id)
              return (
                <label
                  key={option.id}
                  className="flex cursor-pointer items-center gap-2 text-sm"
                >
                  <Checkbox
                    checked={isAssigned}
                    onCheckedChange={(checked) => onChange(option.id, checked === true)}
                    aria-label={`${option.title} für ${moduleTitle}`}
                  />
                  {option.title}
                </label>
              )
            })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function normalizeOrders(settings: ModuleNavigationSetting[]) {
  const order = new Map<string, number>()
  const categoryIds = new Set(
    settings.flatMap((entry) =>
      entry.categories.map((assignment) => assignment.categoryId),
    ),
  )
  for (const categoryId of categoryIds) {
    settings
      .flatMap((entry) =>
        entry.categories
          .filter((assignment) => assignment.categoryId === categoryId)
          .map((assignment) => ({ entry, assignment })),
      )
      .sort((a, b) => a.assignment.sortOrder - b.assignment.sortOrder)
      .forEach(({ entry, assignment }, index) =>
        order.set(`${entry.moduleId}\0${assignment.categoryId}`, index),
      )
  }
  return settings.map((entry) => ({
    ...entry,
    categories: entry.categories.map((assignment) => ({
      ...assignment,
      sortOrder:
        order.get(`${entry.moduleId}\0${assignment.categoryId}`) ?? assignment.sortOrder,
    })),
  }))
}

function CategoryManagement({ categories }: { categories: NavigationCategory[] }) {
  const save = useSaveNavigationCategories()
  const create = useCreateNavigationCategory()
  const [draft, setDraft] = useState<NavigationCategory[] | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [markerText, setMarkerText] = useState('')
  const [colorHex, setColorHex] = useState('#102757')
  const current = draft ?? categories
  const ordered = current.toSorted((left, right) => left.sortOrder - right.sortOrder)

  function update(id: string, change: Partial<NavigationCategory>) {
    setDraft(
      current.map((category) =>
        category.id === id ? { ...category, ...change } : category,
      ),
    )
  }

  function move(id: string, direction: -1 | 1) {
    const index = ordered.findIndex((category) => category.id === id)
    const other = ordered[index + direction]
    if (!other) return

    setDraft(
      current.map((category) => {
        if (category.id === id) return { ...category, sortOrder: other.sortOrder }
        if (category.id === other.id) {
          return {
            ...category,
            sortOrder: ordered[index]?.sortOrder ?? category.sortOrder,
          }
        }
        return category
      }),
    )
  }

  async function submit() {
    try {
      await save.mutateAsync(
        ordered.map((category, index) => ({ ...category, sortOrder: index })),
      )
      setDraft(null)
      toast.success('Rubriken gespeichert')
    } catch {
      toast.error('Rubriken konnten nicht gespeichert werden')
    }
  }

  async function addCategory(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!title.trim()) return

    try {
      await create.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        markerText: markerText.trim(),
        colorHex,
      })
      setTitle('')
      setDescription('')
      setMarkerText('')
      setColorHex('#102757')
      toast.success('Rubrik angelegt')
    } catch {
      toast.error('Rubrik konnte nicht angelegt werden')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rubriken verwalten</CardTitle>
        <p className="text-muted-foreground text-sm">
          Legen Sie eigene Rubriken an, benennen Sie sie um oder deaktivieren Sie sie.
          Deaktivierte Rubriken bleiben für bestehende Zuordnungen erhalten.
        </p>
      </CardHeader>
      <CardContent>
        <fieldset
          disabled={save.isPending || create.isPending}
          className="min-w-0 space-y-6"
        >
          <form
            className="grid gap-4 rounded-lg border bg-muted/20 p-4 sm:grid-cols-2 xl:grid-cols-4 xl:items-center"
            onSubmit={addCategory}
          >
            <div className="grid gap-1.5 sm:col-span-2 xl:col-span-1">
              <Label htmlFor="new-category-title">Neue Rubrik</Label>
              <Input
                id="new-category-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="z. B. Einkauf"
                maxLength={100}
              />
            </div>
            <div className="grid gap-1.5 sm:col-span-2 xl:col-span-3">
              <Label htmlFor="new-category-description">Erklärungstext (optional)</Label>
              <Textarea
                rows={2}
                className="min-h-20 resize-y"
                id="new-category-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Kurze Erklärung für die Startseite"
                maxLength={500}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="new-category-marker">Hintergrundkürzel</Label>
              <Input
                id="new-category-marker"
                value={markerText}
                onChange={(event) => setMarkerText(event.target.value)}
                placeholder="z. B. 07 oder A"
                maxLength={4}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="new-category-color">Farbschema</Label>
              <CategoryColorInput
                id="new-category-color"
                value={colorHex}
                onChange={setColorHex}
                ariaLabel="Farbschema für neue Rubrik"
              />
            </div>
            <Button
              type="submit"
              className="sm:col-span-2"
              disabled={create.isPending || !title.trim()}
            >
              <Plus className="size-4" aria-hidden="true" />
              Rubrik anlegen
            </Button>
          </form>

          <div className="space-y-4">
            {ordered.map((category, index) => (
              <div
                key={category.id}
                className="grid gap-4 rounded-lg border bg-card p-4 sm:grid-cols-2 xl:grid-cols-4 xl:items-center"
              >
                <div className="grid gap-1.5 sm:col-span-2 xl:col-span-1">
                  <Label htmlFor={`category-title-${category.id}`}>Name</Label>
                  <Input
                    id={`category-title-${category.id}`}
                    value={category.title}
                    onChange={(event) =>
                      update(category.id, { title: event.target.value })
                    }
                    maxLength={100}
                  />
                </div>
                <div className="grid gap-1.5 sm:col-span-2 xl:col-span-3">
                  <Label htmlFor={`category-description-${category.id}`}>
                    Erklärungstext
                  </Label>
                  <Textarea
                    rows={2}
                    className="min-h-20 resize-y"
                    id={`category-description-${category.id}`}
                    value={category.description}
                    onChange={(event) =>
                      update(category.id, { description: event.target.value })
                    }
                    maxLength={500}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor={`category-marker-${category.id}`}>
                    Hintergrundkürzel
                  </Label>
                  <Input
                    id={`category-marker-${category.id}`}
                    value={category.markerText}
                    onChange={(event) =>
                      update(category.id, { markerText: event.target.value })
                    }
                    placeholder="01 oder A"
                    maxLength={4}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor={`category-color-${category.id}`}>Farbschema</Label>
                  <CategoryColorInput
                    id={`category-color-${category.id}`}
                    value={category.colorHex}
                    onChange={(colorHex) => update(category.id, { colorHex })}
                    ariaLabel={`Farbschema für ${category.title}`}
                  />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Switch
                    id={`category-active-${category.id}`}
                    checked={category.isActive}
                    onCheckedChange={(isActive) => update(category.id, { isActive })}
                  />
                  <Label htmlFor={`category-active-${category.id}`}>Aktiv</Label>
                </div>
                <div
                  className="flex gap-1 pb-1"
                  aria-label={`Reihenfolge für ${category.title}`}
                >
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={index === 0}
                    onClick={() => move(category.id, -1)}
                    aria-label={`${category.title} nach oben`}
                  >
                    <ArrowUp className="size-4" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={index === ordered.length - 1}
                    onClick={() => move(category.id, 1)}
                    aria-label={`${category.title} nach unten`}
                  >
                    <ArrowDown className="size-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={save.isPending}>
              <Save className="size-4" aria-hidden="true" />
              {save.isPending ? 'Speichert …' : 'Rubriken speichern'}
            </Button>
          </div>
        </fieldset>
      </CardContent>
    </Card>
  )
}

function CategoryColorInput({
  id,
  value,
  onChange,
  ariaLabel,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  ariaLabel: string
}) {
  const safeValue = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#102757'

  return (
    <div className="flex gap-2">
      <label
        className="relative size-10 shrink-0 cursor-pointer overflow-hidden rounded-full border"
        style={{ backgroundColor: safeValue }}
        aria-label={`${ariaLabel} auswählen`}
      >
        <input
          id={id}
          type="color"
          value={safeValue}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className="absolute inset-0 size-full cursor-pointer opacity-0"
          aria-label={ariaLabel}
        />
      </label>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
        pattern="#[0-9A-Fa-f]{6}"
        maxLength={7}
        className="min-w-0 font-mono uppercase"
        aria-label={`${ariaLabel} als Hex-Code`}
        placeholder="#102757"
      />
    </div>
  )
}
